import React from 'react';
import { useTranslation } from 'react-i18next'; 
import { DatePicker } from 'rsuite';
import {
    Divider,
    FormControlLabel,
    Checkbox,
    Radio,
    RadioGroup,
    TextField,
} from '@material-ui/core';
import {
    Disclaimer, InfoButton, LabeledComponent, ObsoleteRangePicker 
} from '@commonClient2/components';
import { applyUkgUserTimeZoneTransformation, formatUTCDateAsIs, getDateTimeFormats } from '@commonClient2/services/ukgDateTime';
import {
    Fields,
    getDuration,
    getEstimatedSubmissionPeriodEndDate,
    isForever,
    isValidDate,
    roundDatePickerValues,
} from './SchedulePeriod.service';
import { EmployeeSubmissionTimeUnit } from '../../../Enums';

const SchedulePeriodSequential = ({
    profile,
    requestPeriod,
    requestPeriodMinDate,
    requestPeriodMaxDate,
    isEstimatingEndDate,
    validationErrors,
    handleDateRangeChange,
    updateSchedulePeriodStore,
}) => {
    const { t } = useTranslation('shiftBidding');
    const userDateTimeFormats = getDateTimeFormats();
    const now = [applyUkgUserTimeZoneTransformation(new Date()), applyUkgUserTimeZoneTransformation(new Date())];

    const {
        schedule,
        originalScheduleTemplates,
        isCreationAllowed,
        isViewAllowed,
    } = profile;

    const {
        submissionPeriodStartDate,
        submissionPeriodEndDate,
        requestPeriodStartDate,
        requestPeriodEndDate,
        dayOfTheWeek,
        startBiddingTime,
        endBiddingTime,
        timePerEmployeeUnit,
        timePerEmployeeValue,
        isEditable,
        isAnytime,
    } = schedule;

    const {
        isActiveWorkplanBid,
        activeWorkplanStartDate,
        activeWorkplanEndDate
    } = originalScheduleTemplates;

    const defaultSubmissionPeriodValues = roundDatePickerValues(Fields.SubmissionPeriod, now);
    const defaultRequestPeriodValues = roundDatePickerValues(Fields.RequestPeriod, requestPeriod);

    const submissionDate = [isValidDate(submissionPeriodStartDate) || defaultSubmissionPeriodValues[0]];
    const rangesRequest = [
        isValidDate(requestPeriodStartDate) || defaultRequestPeriodValues[0],
        isValidDate(requestPeriodEndDate) || defaultRequestPeriodValues[1],
    ];

    const hasOnlyViewAccess = isViewAllowed && !isCreationAllowed;
    const isInputDisabled = !isEditable || hasOnlyViewAccess;
    const isTimeInputDisabled = isInputDisabled || isAnytime;

    /**
     * @param {string} target
     * @param {Date} date
     */
    const handleBiddingTimeSelected = (target, date) => {
        if (date === null) {
            updateSchedulePeriodStore(target, new Date(new Date().setHours(0, 0, 0, 0)));
            return;
        }
        updateSchedulePeriodStore(target, date);
    };

    return (
        <div className="schedule-period-sequential">
            <span className="headline-h-500">{t('form.schedulePeriod.sequentialDaysAndHoursStep.title')}</span>
            <Disclaimer className="text-md-regular">
                {t('form.schedulePeriod.sequentialDaysAndHoursStep.disclaimer')}
            </Disclaimer>
            {Array.from(Array(7).keys()).map((x) => (
                <FormControlLabel
                    key={`biddableDay_${x}`}
                    control={(
                        <Checkbox
                            checked={dayOfTheWeek[x]}
                            // eslint-disable-next-line max-len
                            onChange={(event) => updateSchedulePeriodStore('dayOfTheWeek', { ...dayOfTheWeek, [x]: event.target.checked })}
                            color="primary"
                            disableRipple
                            className="applicableDays-checkbox"
                        />
                    )}
                    label={(
                        <span className="text-md-semibold">
                            {t(`form.schedulePeriod.sequentialDaysAndHoursStep.dayOfWeek.${x}`)}
                        </span>
                    )}
                    disabled={isInputDisabled}
                    className="applicableDays-formControlLabel"
                />
            ))}
            {
                (validationErrors.dayOfTheWeek)
                    ? <span style={{ color: 'red', display: 'flex' }}>{t('form.error.dayOfTheWeek')}</span>
                    : null
            }
            <div className="bidding-time-container">
                <div>
                    <span className="text-md-semibold title">
                        {t('form.schedulePeriod.sequentialDaysAndHoursStep.startTime')}
                    </span>
                    <DatePicker
                        format="hh:mm a"
                        ranges={[]}
                        hideMinutes={(x) => x % 5 !== 0}
                        showMeridian
                        style={{ width: 160 }}
                        value={startBiddingTime}
                        placement="auto"
                        onChange={(date) => handleBiddingTimeSelected('startBiddingTime', date)}
                        disabled={isTimeInputDisabled}
                    />
                </div>
                <div>
                    <span className="text-md-semibold title">
                        {t('form.schedulePeriod.sequentialDaysAndHoursStep.endTime')}
                    </span>
                    <DatePicker
                        format="hh:mm a"
                        ranges={[]}
                        hideMinutes={(x) => x % 5 !== 0}
                        showMeridian
                        style={{ width: 160 }}
                        value={endBiddingTime}
                        placement="auto"
                        onChange={(date) => handleBiddingTimeSelected('endBiddingTime', date)}
                        disabled={isTimeInputDisabled}
                    />
                </div>
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={isAnytime}
                            onChange={(event) => updateSchedulePeriodStore('isAnytime', event.target.checked)}
                            color="primary"
                            disableRipple
                            className="anytime-checkbox"
                        />
                    )}
                    label={(
                        <span className="text-md-semibold">
                            {t('form.schedulePeriod.sequentialDaysAndHoursStep.anytime')}
                        </span>
                    )}
                    disabled={isInputDisabled}
                    className="anytime-formControlLabel"
                />
            </div>
            <InfoButton
                onClick={() => { }}
                text={isAnytime 
                    ? `${t('form.schedulePeriod.duration.title')}: -` 
                    : getDuration(`${t('form.schedulePeriod.duration.title')}:`, startBiddingTime, endBiddingTime)}
                position="left"
            />
            <Divider className="divider" />
            <span className="headline-h-500">{t('form.schedulePeriod.timePerEmployee.title')}</span>
            <RadioGroup
                name="timePerEmployee"
                value={timePerEmployeeUnit}
                className="time-per-employee-radio-group"
                onChange={(event) => updateSchedulePeriodStore('timePerEmployeeUnit', parseInt(event.target.value, 10))}
            >
                <TextField
                    type="number"
                    variant="outlined"
                    label=""
                    value={timePerEmployeeValue}
                    className="time-per-employee-input"
                    onChange={(event) => updateSchedulePeriodStore('timePerEmployeeValue', parseInt(event.target.value, 10))}
                    inputProps={{
                        min: 1,
                    }}
                    disabled={isInputDisabled}
                />
                <FormControlLabel
                    control={<Radio color="primary" />}
                    value={EmployeeSubmissionTimeUnit.Minute}
                    label={t('common.timeUnit.minute', { count: timePerEmployeeValue })}
                    disabled={isInputDisabled}
                />
                <FormControlLabel
                    control={<Radio color="primary" />}
                    value={EmployeeSubmissionTimeUnit.Hour}
                    label={t('common.timeUnit.hour', { count: timePerEmployeeValue })}
                    disabled={isInputDisabled}
                />
            </RadioGroup>
            {
                (validationErrors.timePerEmployeeValue)
                    ? <span style={{ color: 'red' }}>{t('form.error.timePerEmployeeValue')}</span>
                    : null
            }
            {
                (validationErrors.biddingTimeSmallerThanTimePerEmployee)
                    ? <span style={{ color: 'red' }}>{t('form.error.biddingTimeSmallerThanTimePerEmployee')}</span>
                    : null
            }
            <Divider className="divider" />
            <div className="periods-container">
                <div className="submission-start-date">
                    <span className="headline-h-500">{t('form.schedulePeriod.submissionStartDate.title')}</span>
                    <ObsoleteRangePicker
                        onChange={(updatedValue) => handleDateRangeChange(Fields.SubmissionPeriod, updatedValue)}
                        onExit={(_event, picker) => handleDateRangeChange(Fields.SubmissionPeriod, [picker.startDate, picker.endDate])}
                        value={submissionDate}
                        minDate={applyUkgUserTimeZoneTransformation(new Date())}
                        singleDatePicker
                        timePicker
                        placement="up"
                        disabled={isInputDisabled}
                        applyButtonClasses="date-range-picker-apply-button"
                    />
                    {
                        (validationErrors.submissionBeforeRequest)
                            ? <span style={{ color: 'red' }}>{t('form.error.submissionBeforeRequest')}</span>
                            : null
                    }
                </div>
                <div className="request-period">
                    {
                        isActiveWorkplanBid 
                            ? (
                                <LabeledComponent name={t('form.schedulePeriod.requestPeriodStartDate')}>
                                    <ObsoleteRangePicker
                                        onChange={(updatedValue) =>
                                            handleDateRangeChange(Fields.RequestPeriod, [updatedValue[0].toDate(), rangesRequest[1]])}
                                        onExit={(_event, picker) =>
                                            handleDateRangeChange(Fields.RequestPeriod, [picker.startDate.toDate(), rangesRequest[1]])}
                                        value={rangesRequest}
                                        singleDatePicker
                                        minDate={requestPeriodMinDate}
                                        maxDate={requestPeriodMaxDate || false}
                                        placement="up"
                                        disabled={isInputDisabled}
                                        applyButtonClasses="date-range-picker-apply-button"
                                    />
                                </LabeledComponent>
                            ) : (
                                <LabeledComponent name={t('form.schedulePeriod.requestPeriod')}>
                                    <ObsoleteRangePicker
                                        onChange={(updatedValue) =>
                                            handleDateRangeChange(Fields.RequestPeriod, updatedValue)}
                                        onExit={(_event, picker) =>
                                            handleDateRangeChange(Fields.RequestPeriod, [picker.startDate, picker.endDate])}
                                        value={rangesRequest}
                                        minDate={requestPeriodMinDate}
                                        placement="up"
                                        disabled={isInputDisabled}
                                        applyButtonClasses="date-range-picker-apply-button"
                                    />
                                </LabeledComponent>
                            )
                    }
                    {
                        (validationErrors.requestPeriod)
                            ? <span style={{ color: 'red' }}>{t('form.error.requestPeriodWarning')}</span>
                            : null
                    }
                </div>
            </div>
            <InfoButton
                onClick={() => { }}
                text={getEstimatedSubmissionPeriodEndDate(
                    t('form.schedulePeriod.submissionStartDate.estimatedEndDate'),
                    isEstimatingEndDate,
                    submissionPeriodEndDate,
                )}
                position="left"
            />
            {
                isActiveWorkplanBid ? (
                    <div>
                        <Disclaimer className="workplan-period-disclaimer">
                            {
                                t(
                                    'activeWorkplanDates.disclaimer',
                                    {
                                        startDate: formatUTCDateAsIs(
                                            new Date(activeWorkplanStartDate * 1000),
                                            userDateTimeFormats.ShortDateFormat,
                                        ),
                                        endDate: (
                                            isForever(activeWorkplanEndDate)
                                                ? t('form.activeWorkplanDates.forever')
                                                : formatUTCDateAsIs(
                                                    new Date(activeWorkplanEndDate * 1000),
                                                    userDateTimeFormats.ShortDateFormat,
                                                )
                                        ),
                                    },
                                )
                            }
                        </Disclaimer>
                        <Disclaimer className="workplan-period-disclaimer">
                            {t('form.activeWorkplanDates.disclaimer2')}
                        </Disclaimer>
                    </div>
                ) : null
            }
        </div>
    );
};

export default SchedulePeriodSequential;
